import get from 'lodash.get';
import React, { useContext, useEffect, useState } from 'react';
import { Button, Col, Row } from 'react-bootstrap';
import { BsBriefcase } from 'react-icons/bs';
import { toast } from 'react-toastify';

import apiUtil from 'src/api/businessOnboarding';
import CustomToast from 'src/components/CustomToast';
import Loader from 'src/components/Loader';
import NoDataComponent from 'src/components/NoDataComponent';
import PaginationLayout from 'src/components/PaginationLayout';
import {
  CLAIM_BUSINESS_ERROR,
  CLAIM_BUSINESS_ERROR_CLAIMED,
  fetchingError,
  SOMETHING_WENT_WRONG,
} from 'src/containers/Core/Messages';
import { BUSINESS_ONBOARDING_STEP } from '.';
import { AppContext } from '../Core/AppContext';
import { AppState } from '../Core/AppReducer';
import BusinessItem from './Components/BusinessItem';
import BusinessProfile from './Components/BusinessProfile';

type MatchingBusinessesProps = {
  setOnboardingStep: (step: BUSINESS_ONBOARDING_STEP, businessId?: string) => void;
};

const MatchingBusinesses: React.FunctionComponent<MatchingBusinessesProps> = ({ setOnboardingStep }) => {
  const [loading, setLoading] = useState(false);
  const { store } = useContext(AppContext);
  const { businessOnboardingDetails }: AppState = store;
  const businessUuid = get(businessOnboardingDetails, 'business.uuid');

  const [showProfileModal, setShowProfileModal] = useState<EntityFields>(null);
  const [businessRelations, setBusinessRelations] = useState<EntityFields[]>([]);

  if (!businessUuid) {
    return null;
  }

  useEffect(() => {
    fetchBusinesses();
  }, []);

  const fetchBusinesses = async () => {
    try {
      setLoading(true);
      const payload = await apiUtil.getMatchingBusinesses(businessUuid);
      if (payload.length === 0) {
        setOnboardingStep(BUSINESS_ONBOARDING_STEP.ONBOARD_BUSINESS_TUTORIAL);
      } else {
        setBusinessRelations(payload);
      }
    } catch (error) {
      toast(<CustomToast description={fetchingError('Businesses')} />);
    } finally {
      setLoading(false);
    }
  };

  const onBusinessSelect = async () => {
    try {
      const payload = await apiUtil.claimBusiness(businessOnboardingDetails.uuid, showProfileModal.uuid);
      if (!get(payload, 'claimedSuccessfully')) {
        toast(<CustomToast description={CLAIM_BUSINESS_ERROR_CLAIMED} />);
      } else {
        setOnboardingStep(BUSINESS_ONBOARDING_STEP.ONBOARD_BUSINESS_TUTORIAL, get(payload, 'business.uuid'));
      }
    } catch {
      toast(<CustomToast title={SOMETHING_WENT_WRONG} description={CLAIM_BUSINESS_ERROR} />);
    }
  };

  if (businessRelations.length === 0 && loading) {
    return <Loader />;
  }

  const renderList = () => {
    return (
      <PaginationLayout
        showMore={false}
        loadMore={() => null}
        loading={loading}
        style={{ maxHeight: 'calc(100vh - 200px)', overflow: 'auto' }}
      >
        {businessRelations.length ? null : (
          <NoDataComponent className="h-100 pt-3" Icon={BsBriefcase} title="No businesses found." />
        )}
        {businessRelations.map((business: EntityFields, index: number) => {
          return (
            <div className="position-relative" key={index}>
              <BusinessItem
                entity={business}
                onClick={() => setShowProfileModal(business)}
                contentClassName="border-bottom"
              />
            </div>
          );
        })}
      </PaginationLayout>
    );
  };

  return (
    <div>
      <BusinessProfile
        show={!!showProfileModal}
        onHide={() => setShowProfileModal(null)}
        business={showProfileModal}
        onSelect={onBusinessSelect}
      />
      <div className="overflow-auto h-100 p-3">
        <div className="d-flex flex-column">
          <strong>We found the following businesses that match the details you have given.</strong>
          <small className="text-muted mb-1">
            Click on the profile if you wish to continue with one of these profiles.
          </small>
        </div>
        <Row>
          <Col sm={12} md={6}>
            {renderList()}
          </Col>
        </Row>
      </div>
      <div className="ml-3">
        <div className="text-muted small">
          Select your business profile from the list above, or create a new profile.
        </div>
        <Button className="mt-2" onClick={() => setOnboardingStep(BUSINESS_ONBOARDING_STEP.ONBOARD_BUSINESS_TUTORIAL)}>
          <small>Continue with my own</small>
        </Button>
      </div>
    </div>
  );
};

export default MatchingBusinesses;
